import { auth, app } from "../../config/firebase";
import { signInWithCustomToken } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { migrateGuestToNewUser } from "../firestore/migrateGuestToNewUser";
import { getRouteAfterAuth } from "./getRouteAfterAuth";
import { getAccount, recordLogin } from "../../api/accountApi";

export interface TestAccountBypassParams {
  email: string;
  setUserId: (uid: string) => void;
  setEmail: (email: string) => void;
  setMoods: (moods: any[]) => void;
  setCauses: (causes: any[]) => void;
  refreshData: (uid: string, scope: string) => Promise<void>;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}

export interface TestAccountBypassResult {
  success: boolean;
  error?: string;
}

type CreateTestAccountTokenResponse = { customToken: string; uid?: string };

function buildAuthErrorMessage(authError: any): string {
  let errorMsg = "Failed to authenticate test account. ";
  if (
    authError?.code === "permission-denied" ||
    authError?.message?.includes("permission-denied")
  ) {
    errorMsg +=
      "This email or hostname is not authorized for test account bypass.";
  } else if (authError?.code === "functions/not-found") {
    errorMsg +=
      "Cloud Function not found. Please deploy the createTestAccountToken function.";
  } else {
    errorMsg += authError?.message || "Unknown error occurred.";
  }
  return errorMsg;
}

async function authenticateOrGetUid(email: string): Promise<{ uid: string }> {
  const existing = auth.currentUser;
  if (existing) return { uid: existing.uid };

  const functions = getFunctions(app, "europe-west1");
  const createTestAccountToken = httpsCallable(
    functions,
    "createTestAccountToken"
  );

  const result = await createTestAccountToken({ email });
  const { customToken, uid } = result.data as CreateTestAccountTokenResponse;

  const userCredential = await signInWithCustomToken(auth, customToken);
  const signedIn = userCredential.user;

  return { uid: uid || signedIn.uid };
}

function applyLocalState(params: {
  uid: string;
  email: string;
  setUserId: (uid: string) => void;
  setEmail: (email: string) => void;
  setMoods: (moods: any[]) => void;
  setCauses: (causes: any[]) => void;
}): void {
  const { uid, email, setUserId, setEmail, setMoods, setCauses } = params;

  localStorage.setItem("tujud_uid", uid);
  localStorage.setItem("emailForSignIn", email);

  setMoods([]);
  setCauses([]);
  setUserId(uid);
  setEmail(email);
}

function kickOffPostLoginEffects(params: {
  uid: string;
  email: string;
  refreshData: (uid: string, scope: string) => Promise<void>;
  navigate: (path: string, options?: { replace?: boolean }) => void;
}): void {
  const { uid, email, refreshData, navigate } = params;

  setTimeout(async () => {
    try {
      await refreshData(uid, "all");
    } catch (error) {
      console.warn("Failed to refresh data after login:", error);
    }
  }, 100);

  setTimeout(async () => {
    try {
      const routeResult = await getRouteAfterAuth({ uid, email });
      navigate(routeResult.route, { replace: true });
    } catch (error) {
      console.error("Error determining route after authentication:", error);
      navigate("/app/home", { replace: true });
    }
  }, 1000);
}

export async function handleTestAccountBypass(
  params: TestAccountBypassParams
): Promise<TestAccountBypassResult> {
  const {
    email,
    setUserId,
    setEmail,
    setMoods,
    setCauses,
    refreshData,
    navigate,
  } = params;

  try {
    let uid: string;

    try {
      const authRes = await authenticateOrGetUid(email);
      uid = authRes.uid;
    } catch (authError: any) {
      console.error("🔧 Test account bypass error:", authError);
      return { success: false, error: buildAuthErrorMessage(authError) };
    }

    const existingAccount = await getAccount().catch(() => null);
    const isExistingUser = !!existingAccount;

    // Creates the account if missing and stamps last_login_at - preserves
    // organizationId/isLead/defaultLanguage automatically either way.
    await recordLogin();

    if (!isExistingUser) {
      migrateGuestToNewUser(uid);
    }

    applyLocalState({ uid, email, setUserId, setEmail, setMoods, setCauses });
    kickOffPostLoginEffects({ uid, email, refreshData, navigate });

    return { success: true };
  } catch (error: any) {
    console.error("🔧 Test account bypass error:", error);
    return {
      success: false,
      error: error?.message || "Test account setup error",
    };
  }
}
